import React, {useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import useParams from 'components/data/URLParams/useParams'
import UserSideMenuWithStore from 'components/UserSideMenuWithStore'
import {Component} from 'middlewares/sideEffects/analytics'
import {
  getAnonymousId,
  getUserCountryWithFallback,
  getUserCurrencyWithFallback
} from 'modules/meta/selectors'
import {changePreferencesVisibility} from 'modules/overlay/actions'
import {getPreferencesVisibility} from 'modules/overlay/selectors'
import getNewLocationUrl from 'utils/getNewLocationUrl'

import {Button} from '@vio/atlas/Button'
import {Divider} from '@vio/atlas/Divider'
import {NAV_BAR_MOBILE_HEIGHT, NavBar, NavBarTitle} from '@vio/atlas/NavBar'
import {Overlay} from '@vio/atlas/Overlay'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getDevicePlatform} from '@vio/core/src/_web/utils/userAgent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {selectIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {getMyBookingsUrl} from '@vio/core/src/brand/utils'
import {
  getEnabledCurrencies,
  splitPopularAndOtherCurrencies
} from '@vio/core/src/localization/business/currency'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import availableLanguages from '@vio/core/src/localization/services/locale'
import {useLogOut} from '@vio/shared/src/authentication/hooks/useLogOut'
import {NavBarBackButton} from '@vio/shared/src/navBar'
import {PreferenceSelector} from '@vio/shared/src/user/preferences/PreferenceSelector'

interface PropsType {
  currentLanguage: string
  hasLanguageSelector?: boolean
  hasCurrencySelector?: boolean
  preferenceTitle?: JSX.Element
  hideMyBooking?: boolean
  hideShareYourFeedback?: boolean
  hideReferral?: boolean
  pageName?: Page
  onCurrencyChange?: (currency: string) => void
}

const OverlaySection = styled.section`
  height: calc(100% - ${NAV_BAR_MOBILE_HEIGHT}px);
  display: flex;
  flex-direction: column;
`

const SignOutWrapper = styled.div`
  margin-top: auto;
  padding: ${cssTheme.layout.spacing.s300} ${cssTheme.layout.spacing.s500}
    ${cssTheme.layout.spacing.s600} ${cssTheme.layout.spacing.s500};
`

export const PreferencesOverlay = ({
  currentLanguage,
  hasLanguageSelector,
  hasCurrencySelector,
  preferenceTitle,
  hideMyBooking,
  hideReferral,
  pageName,
  onCurrencyChange
}: PropsType) => {
  const currentCurrency = useSelector(getUserCurrencyWithFallback)
  const isPaymentPage = pageName === Page.Payment
  const dispatch = useDispatch()
  const urlParams = useParams()
  const {brand} = useBrand()
  const SEARCH_URL = brand.siteAddresses.address

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isPreferencesVisible = Boolean(useSelector(getPreferencesVisibility))
  const devicePlatform = getDevicePlatform()
  const logOut = useLogOut()
  const userCountry = useSelector(getUserCountryWithFallback)
  const {locale, currency} = urlParams
  const anonymousId = useSelector(getAnonymousId)
  const myBookingsUrl = getMyBookingsUrl({
    brand,
    appEnv: process.env.NEXT_PUBLIC_ENV || '',
    anonymousId: anonymousId ?? undefined,
    userCountry,
    locale,
    currency
  })

  const handleLogoutClick = useCallback(async () => logOut(), [logOut])

  const enabledCurrencies = process.env.NEXT_PUBLIC_ENABLED_CURRENCIES
    ? getEnabledCurrencies(process.env.NEXT_PUBLIC_ENABLED_CURRENCIES)
    : []

  const {currenciesList, popularCurrenciesList} =
    splitPopularAndOtherCurrencies(enabledCurrencies)

  const handleHidePreferences = () =>
    dispatch(changePreferencesVisibility(false, false)())

  const redirectToAlerts = () => {
    window.location.href = `${SEARCH_URL}/user/alerts`
  }

  const redirectToReferral = () => {
    window.location.href = `${SEARCH_URL}/user/membership/referral`
  }

  const onChangeLanguage = async (language: string) => {
    if (isPaymentPage) {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.LanguageChange,
          action: Action.Submitted,
          page: Page.Payment,
          component: Component.Preferences,
          payload: {newValue: language}
        })
      )
    }

    setTimeout(() => {
      const newUrl = getNewLocationUrl({
        url: '',
        search: {locale: language}
      })
      window.location.href = newUrl
    }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
  }

  const onChangeCurrency = (currency: string) => {
    if (isPaymentPage) {
      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.CurrencyChange,
          action: Action.Submitted,
          page: Page.Payment,
          component: Component.Preferences,
          payload: {newValue: currency}
        })
      )
      onCurrencyChange?.(currency)
    } else {
      setTimeout(() => {
        const newUrl = getNewLocationUrl({
          url: '',
          search: {
            currency,
            price: undefined,
            originalCurrency: undefined
          }
        })

        window.location.href = newUrl
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
    }
  }

  return (
    <Overlay isOpen={isPreferencesVisible}>
      <NavBar
        centerAligned
        withDivider
        startContent={<NavBarBackButton onClick={handleHidePreferences} />}
        centerContent={
          <NavBarTitle>
            {preferenceTitle ? (
              preferenceTitle
            ) : (
              <FormattedMessageWrapper id="profile" defaultMessage="Profile" />
            )}
          </NavBarTitle>
        }
      />
      <OverlaySection>
        <UserSideMenuWithStore />
        <Divider />
        <PreferenceSelector
          availableLanguages={availableLanguages}
          currenciesList={currenciesList}
          currentLanguage={currentLanguage}
          currentCurrency={currentCurrency}
          devicePlatform={devicePlatform}
          hasCurrencySelector={hasCurrencySelector}
          hasLanguageSelector={hasLanguageSelector}
          myBookingsUrl={isAuthenticated ? myBookingsUrl : ''}
          popularCurrenciesList={popularCurrenciesList}
          showShareYourFeedback={false}
          onChangeCurrency={onChangeCurrency}
          onChangeLanguage={onChangeLanguage}
          onClickAlerts={redirectToAlerts}
          onClickReferral={redirectToReferral}
          hideMyBooking={hideMyBooking}
          hideShareYourFeedback={true}
          hideReferral={hideReferral}
        />
        {isAuthenticated && (
          <SignOutWrapper>
            <Button
              fullWidth
              variant="danger"
              size="lg"
              onClick={handleLogoutClick}
            >
              <FormattedMessageWrapper
                dataId="LogOutProfileLink"
                id="signOut"
                defaultMessage="Sign Out"
              />
            </Button>
          </SignOutWrapper>
        )}
      </OverlaySection>
    </Overlay>
  )
}
